import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// AIRDROP report viewer — the SHARED presentation primitive (card #1902).
//
// One controller drives the read-only consumption experience everywhere a deck
// is shown: the show page, the shareable page, and the deck-editor Preview tab.
// It is presentation-only — no editing. It operates on the slide "pages" found
// within its own scope (.airdrop-viewer__slide wrappers, each holding an
// .airdrop-slide-scale > .airdrop-slide), regardless of how those slides were
// produced (server ERB partials OR the editor's JS-built DOM). It never reaches
// into deck-editor state.
//
// Mechanics are modelled on the original Python slidepack engine
// (airdrop/slidepacks/engine/slidepack.js) but reimplemented natively:
//   - Fullscreen via the Fullscreen API, with a fullscreenchange listener that
//     syncs UI state when the user exits with Esc/F11.
//   - Slideshow: one slide at a time, scaled to fill the viewport
//     (Math.min(innerWidth/1280, innerHeight/720)), all chrome hidden (the top
//     toolbar entirely, the bottom pres-nav cluster idle-fades — card #2171),
//     click or keys advance, Escape exits. Entering slideshow auto-requests
//     fullscreen.
//   - Nav keys: ArrowRight/Space/PageDown → next; ArrowLeft/PageUp → prev;
//     Home/End → first/last; Escape → exit a mode.
//   - Counter "N / M" + a progress bar, both excluding hidden slides.
//   - Print to PDF (the @media print rules in application.css do the layout;
//     this just triggers window.print()).
//   - Filmstrip: scaled thumbnail clones, click-to-jump, active highlight.
//   - Deep-linking via #slide-N hash on load + as you navigate.
//
// Feature flags arrive as Boolean values from the _viewer partial so each
// consumer enables only the chrome it wants (the show page keeps copy-to-PNG,
// the shareable page drops authoring affordances, etc.).
// Canvas size is a per-deck setting (ADR-0077). The deck's dimensions arrive as
// the canvasW / canvasH Stimulus values (rendered from the deck server-side);
// both default to the slidepack canvas (1280x720) so a viewer mounted without
// them behaves exactly as before.
const DEFAULT_CANVAS_W = 1280;
const DEFAULT_CANVAS_H = 720;
// How long the pointer must sit idle while presenting before the bottom
// pres-nav cluster fades (card #2171 — PowerPoint-style auto-hide chrome).
const PRES_IDLE_MS = 3000;
// Card #2633 Parts 3 & 4: insert/remove slide animation duration. MUST match
// the grid-template-rows transition duration on .airdrop-viewer__slide--
// animating in application.css (the JS side has no reliable cross-browser
// way to read a CSS transition-duration back out, so this is kept in step by
// hand -- same convention PRES_IDLE_MS above already follows, no CSS twin
// either). Used as the fallback timeout in waitForTransitionEnd, in case a
// transitionend event never fires (e.g. the tab is backgrounded and
// rAF/transitions are throttled).
const SLIDE_ANIMATION_MS = 400;
// Card #2643 refinement -- floor (not fixed delay) for how long the manual
// refresh's loading state (blank deck + spinner pill) stays on screen,
// regardless of how fast DecksController#refresh_slides actually responds.
// Jay: the fetch resolves in ~50ms locally, too fast to read as "something
// happened" -- total visible loading time is max(actualFetchDuration,
// MIN_REFRESH_DISPLAY_MS), never less. See waitOutMinimumDisplay.
const MIN_REFRESH_DISPLAY_MS = 1500;

export default class extends Controller {
  static targets = [
    "deck", "slide", "counter", "progressBar",
    "fullscreenButton", "slideshowButton",
    "prevButton", "nextButton",
    "filmstrip", "filmstripPanel", "thumb",
    "reviewFilterButton", "feedbackFilterButton",
    "refreshOverlay", "toolbarSticky"
  ];

  static values = {
    fullscreen: { type: Boolean, default: false },
    slideshow: { type: Boolean, default: false },
    nav: { type: Boolean, default: true },
    counter: { type: Boolean, default: true },
    filmstrip: { type: Boolean, default: false },
    deepLink: { type: Boolean, default: true },
    kiosk: { type: Boolean, default: false },
    canvasW: { type: Number, default: DEFAULT_CANVAS_W },
    canvasH: { type: Number, default: DEFAULT_CANVAS_H },
    // Show-page review-status filter (card #2268) — "" (All) | "not_reviewed" |
    // "deciding" | "approved" | "rejected". Initial value arrives from the
    // server (DecksController#show's whitelisted ?review_status= param,
    // threaded through _viewer.html.erb), then is driven client-side by
    // setReviewFilter below. Every OTHER consumer (present/shareable/editor
    // Preview) never sets this attribute, so it stays "" (no-op) there.
    reviewFilter: { type: String, default: "" },
    // Show-page feedback-state filter (card #2536) — "" (no filter) |
    // "all" | "unresolved" | "resolved". A SECOND, fully independent filter
    // dimension composed with reviewFilter above by AND logic (Jay: "the
    // filters should also work together") — see matchesFilters below. Retires
    // the standalone feedback-filter Stimulus controller + revealed slide-list
    // UI (card #2524 follow-up) in favour of the SAME mechanism the
    // review-status pills already use: the real slides in the deck flow are
    // filtered directly, not a separate list.
    feedbackFilter: { type: String, default: "" }
  };

  // The deck's canvas dimensions (ADR-0077), defaulting to the slidepack canvas
  // when the value is absent or non-positive, so a missing attribute is inert.
  get canvasW() { return this.canvasWValue > 0 ? this.canvasWValue : DEFAULT_CANVAS_W; }
  get canvasH() { return this.canvasHValue > 0 ? this.canvasHValue : DEFAULT_CANVAS_H; }

  connect() {
    this.index = 0;
    this.presenting = false;

    // Card #2643 correction -- true only for the span of reloadPage()'s own
    // fetch below, read synchronously by handleBeforeStreamRender at the
    // instant its stream lands, so that SAME "deck_#{id}_slides" replace
    // target can tell "this is my manual refresh" apart from "this is the
    // live structural broadcast" and pick the right treatment (plain
    // fade-in vs animateSlidesExiting/animateSlideEntrance). See
    // handleBeforeStreamRender and reloadPage for the two ends of this.
    this.pendingManualReload = false;

    // Card #2633 Part 3: the set of slide uuids this viewer has ever
    // rendered, seeded from whatever is already on the page. Diffed against
    // in animateSlideEntrance below to tell a genuinely NEW slide (arriving
    // via a structural broadcast_deck_refresh! replace) apart from every
    // OTHER slide that also re-fires slideTargetConnected on the same
    // replace (Turbo destroys/recreates the whole decks/_slide_list.html.erb
    // wrapper, not just the delta -- see that callback's own comment).
    this.knownSlideIds = new Set(
      this.hasSlideTarget
        ? this.slideTargets.map((slide) => slide.dataset.slideUuid).filter(Boolean)
        : []
    );

    // Card #2633 Part 4: intercept the whole-list structural replace BEFORE
    // Turbo applies it, so a slide about to disappear gets a chance to
    // shrink out first. See handleBeforeStreamRender for the narrow scoping
    // (only OUR "deck_#{id}_slides" target) and animateSlidesExiting for the
    // animation itself.
    this.boundBeforeStreamRender = (event) => this.handleBeforeStreamRender(event);
    document.addEventListener("turbo:before-stream-render", this.boundBeforeStreamRender);

    // Card #2633 fix -- discovered verifying reloadPage() (the manual
    // refresh button, Part 2): application.html.erb sets
    // `turbo_refreshes_with method: :morph`, so `Turbo.visit(url, { action:
    // "replace" })` on the SAME URL triggers a MORPH refresh, not a hard
    // Drive navigation. A morph patches this controller's own root element
    // (and the counter/progress-bar spans, which the server always renders
    // with their static placeholder text/width, e.g. "0 / 0") IN PLACE
    // rather than destroying and recreating it -- so connect() never runs
    // again to re-render, and the toolbar visibly reverts to its blank
    // initial state (confirmed live: counter stuck at "0 / 0" after every
    // refresh-button click, despite the slides themselves still being
    // present and correct). Turbo dispatches the DISTINCT `turbo:morph`
    // event once the morph completes; re-running refresh() re-derives the
    // counter/progress bar/filmstrip/nav-button state from the CURRENT DOM,
    // exactly as connect()'s own refresh() call does on first render. See
    // persist_tabs_controller.js for the sibling fix to the SAME root cause
    // (Report tab visibility), found in the same investigation.
    //
    // Card #2643 UPDATE: reloadPage no longer causes a morph at all (it fetches
    // DecksController#refresh_slides and applies a scoped Turbo Stream instead
    // of visiting the page) -- and a codebase-wide check found no OTHER
    // same-URL Turbo Drive visit or `broadcasts_refreshes`-style refresh
    // trigger on this page. This listener is very likely dead code for the
    // deck show page specifically, now. Left in place rather than removed
    // speculatively: it's a cheap no-op the rest of the time, and this
    // controller is also mounted on the TV kiosk/shareable/present/editor-
    // Preview pages, which weren't individually audited for some other,
    // unrelated same-URL morph trigger of their own.
    this.boundMorph = () => this.refresh();
    document.addEventListener("turbo:morph", this.boundMorph);

    // Keyboard: one consolidated handler at the document level. It is guarded so
    // it never fires while a text field / contentEditable is focused, and only
    // acts on navigation when this viewer is the active surface (presenting, or
    // the sole viewer on the page).
    this.boundKeydown = (event) => this.handleKeydown(event);
    document.addEventListener("keydown", this.boundKeydown);

    // Keep slideshow + fullscreen UI honest when the user exits externally.
    this.boundFullscreenChange = () => this.syncFullscreenState();
    document.addEventListener("fullscreenchange", this.boundFullscreenChange);

    this.boundResize = () => { if (this.presenting) this.applyPresScale(); };
    window.addEventListener("resize", this.boundResize);

    // Idle-fade for the bottom pres-nav cluster while presenting (card #2171).
    // Listeners are bound once here but only act while this.presenting is true
    // (see resetIdleTimer), so they are cheap no-ops the rest of the time.
    this.boundResetIdleTimer = () => this.resetIdleTimer();
    document.addEventListener("mousemove", this.boundResetIdleTimer);
    document.addEventListener("touchstart", this.boundResetIdleTimer);

    // Keep a slide's data-review-status in sync after a LIVE status change
    // (card #2268 follow-up) — decks/_slide_review_actions dispatches this
    // whenever it (re)connects, including every set_status turbo_stream
    // replace, so the review-status filter re-evaluates against the CURRENT
    // status instead of the value captured once at initial render.
    this.boundSlideReviewUpdated = (event) => this.onSlideReviewUpdated(event);
    document.addEventListener("slide-review:updated", this.boundSlideReviewUpdated);

    // Live counterpart for the feedback-state filter (card #2536) — same
    // pattern as slide-review:updated above, see onSlideFeedbackUpdated.
    this.boundSlideFeedbackUpdated = (event) => this.onSlideFeedbackUpdated(event);
    document.addEventListener("slide-feedback:updated", this.boundSlideFeedbackUpdated);

    // Deep-link target on load (#slide-3), once the slides exist.
    this.refresh();
    this.jumpToHash();
    if (this.kioskValue) requestAnimationFrame(() => this.enterSlideshow());

    // Auto-print hand-off (#2071): the chromed show page links to the bare
    // /present view with ?print=1; only that view carries the param, so this
    // fires there and never on the show page. Defers to the window load event,
    // THEN awaits waitUntilPrintReady() (#2167) so custom webfonts and any
    // analytics iframe get a chance to paint before the dialog opens — `load`
    // alone is not enough, see that method's comment. Guarded so it can only
    // fire once. Never auto-print on an unattended kiosk screen.
    this.maybeAutoPrint();

    // React to hash changes (back/forward, manual edits).
    this.boundHashChange = () => this.jumpToHash();
    window.addEventListener("hashchange", this.boundHashChange);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    document.removeEventListener("fullscreenchange", this.boundFullscreenChange);
    window.removeEventListener("resize", this.boundResize);
    window.removeEventListener("hashchange", this.boundHashChange);
    document.removeEventListener("mousemove", this.boundResetIdleTimer);
    document.removeEventListener("touchstart", this.boundResetIdleTimer);
    document.removeEventListener("slide-review:updated", this.boundSlideReviewUpdated);
    document.removeEventListener("slide-feedback:updated", this.boundSlideFeedbackUpdated);
    document.removeEventListener("turbo:before-stream-render", this.boundBeforeStreamRender);
    document.removeEventListener("turbo:morph", this.boundMorph);
    clearTimeout(this.idleTimer);
    this.filmstripObserver?.disconnect();
    if (this.presenting) this.exitSlideshow();
  }

  // ---- State / scanning --------------------------------------------------

  // (Re)scan the slide pages. Called on connect and whenever the deck content
  // changes underneath the viewer (the Preview tab rebuilds slides on entry and
  // calls this). Clamps the active index and re-renders all chrome.
  refresh() {
    this.applyFilters();
    if (this.index >= this.visibleSlides().length) {
      this.index = Math.max(0, this.visibleSlides().length - 1);
    }
    this.buildFilmstrip();
    this.render();
  }

  // Stimulus lifecycle callback (card #2633) -- fires automatically whenever
  // an element matching the "slide" target enters this controller's scope,
  // including a Turbo Stream replace of decks/_slide_list.html.erb (a
  // structural insert/reorder broadcast, see
  // AiTools::BaseService#broadcast_deck_refresh!). Without this, swapping in
  // a new/reordered set of slide elements would update the DOM but leave the
  // counter, filmstrip, and prev/next clamping stuck on the PREVIOUS slide
  // count until something else called refresh() -- this is that something.
  // Guarded on this.index being set (undefined during the very first scope
  // scan, before connect() runs) so the initial page load's own refresh()
  // call in connect() stays the single source of truth for first render, the
  // same guard onSlideReviewUpdated/onSlideFeedbackUpdated use above. A whole-
  // list replace re-adds every slide element at once, so this fires once per
  // slide -- refresh() is cheap and idempotent, so that's harmless.
  slideTargetConnected(element) {
    if (this.index === undefined) return;
    this.animateSlideEntrance(element);
    this.refresh();
  }

  // Card #2633 Part 3 -- entrance animation ("grow in", not a pop) for a
  // genuinely NEW slide arriving via a structural broadcast_deck_refresh!
  // replace, as opposed to every OTHER slide on the page also re-firing
  // slideTargetConnected on the very same replace (see that callback's own
  // comment for why). Diffs this element's data-slide-uuid against
  // this.knownSlideIds (seeded from the DOM in connect(), kept current here)
  // -- only an id this viewer has genuinely never rendered before counts as
  // new; a slide reappearing after being hidden by the review/feedback
  // filters is NOT new and must render at rest, no transition, no flash.
  //
  // Slides with no data-slide-uuid (copy=false consumers: present/
  // shareable/kiosk/editor Preview) are skipped outright -- there is no
  // stable identity to diff against there (the DOM id is POSITION-based,
  // id="slide-N", which shifts on every insert/reorder/remove), and none of
  // those consumers subscribe to the live structural broadcast this
  // animation exists for anyway.
  //
  // Reuses the house grid-template-rows collapse/expand trick verbatim
  // (application.css, the Notes/Feedback panel's own mechanism) via the
  // .airdrop-viewer__slide--animating / .is-open class pair: starts
  // collapsed (0fr, hidden), then flips to .is-open on the next animation
  // frame so the transition actually plays (a class added and immediately
  // followed by another added in the SAME frame never transitions -- the
  // standard two-frame trick). Both animation classes are stripped once the
  // transition finishes, returning the slide to its normal (non-grid) block
  // layout with zero ongoing cost -- see the CSS comment for why that's a
  // no-op visually (1fr's row height already equals the content's natural
  // height).
  animateSlideEntrance(element) {
    const uuid = element.dataset.slideUuid;
    if (!uuid) return;

    const isNew = !this.knownSlideIds.has(uuid);
    this.knownSlideIds.add(uuid);
    if (!isNew) return;

    element.classList.add("airdrop-viewer__slide--animating");
    requestAnimationFrame(() => {
      element.classList.add("is-open");
      this.waitForTransitionEnd(element).then(() => {
        element.classList.remove("airdrop-viewer__slide--animating", "is-open");
      });
    });
  }

  // Card #2633 Part 4 -- intercepts the whole-list structural replace
  // (broadcast_deck_refresh!'s own turbo_stream.replace on
  // "deck_#{id}_slides") BEFORE Turbo applies it, via the documented
  // turbo:before-stream-render extension point (Turbo 8 -- StreamElement
  // dispatches this on itself, bubbling to document, with
  // event.detail.render overridable; Turbo awaits it before proceeding --
  // see @hotwired/turbo's stream_element.js#render). Narrowly scoped to
  // stream messages targeting OUR OWN slide list -- every other broadcast on
  // this page (a single-slide broadcast_slide_refresh! replace, a toolbar
  // flash) targets a different id and is left alone, untouched, falling
  // through to Turbo's normal default rendering.
  handleBeforeStreamRender(event) {
    const stream = event.target;
    if (!this.hasDeckTarget) return;
    if (stream.target !== this.deckTarget.id) return;

    const defaultRender = event.detail.render;
    // Card #2643 correction -- captured HERE, synchronously, at the moment
    // THIS specific stream lands. Turbo.renderStreamMessage in reloadPage
    // below dispatches turbo:before-stream-render synchronously as part of
    // that very call, well before reloadPage's own `finally` block gets a
    // chance to reset the flag -- so closing over a local const is safe
    // regardless of when pendingManualReload flips back to false.
    const isManualReload = this.pendingManualReload;

    event.detail.render = async (streamElement) => {
      if (isManualReload) {
        // Manual refresh (card #2643 correction) -- every slide survives
        // untouched: no exit, no re-entrance, just a plain container
        // fade-in once the fresh content actually lands. See
        // prepareDeckFadeIn/playDeckFadeIn below. This is the fix for the
        // flash Jay saw: the OLD behaviour reset knownSlideIds to empty so
        // every slide replayed animateSlideEntrance's grid-rows grow-in at
        // once, which blanked the whole slide-list area for a beat.
        this.prepareDeckFadeIn(streamElement);
        await defaultRender(streamElement);
        this.hideRefreshOverlay();
        this.playDeckFadeIn();
      } else {
        // Automatic structural broadcast (card #2633, UNCHANGED by #2643) --
        // a genuine insert/remove, so slides actually leaving still get
        // their own shrink-out first, and slideTargetConnected still plays
        // animateSlideEntrance for whatever's genuinely new.
        await this.animateSlidesExiting(streamElement);
        await defaultRender(streamElement);
      }
    };
  }

  // Diffs the incoming replacement HTML's slide uuids against what's
  // CURRENTLY on screen. Any uuid about to disappear gets the reverse of
  // animateSlideEntrance -- starts at rest (open), then collapses to 0fr --
  // and this awaits every exiting slide's transition before letting Turbo's
  // default renderer swap the DOM out from under it, so the shrink is
  // visible rather than instantaneous. Slides that survive the replace are
  // untouched here; their own re-render is handled normally by Turbo/
  // slideTargetConnected as always.
  async animateSlidesExiting(streamElement) {
    if (!this.hasDeckTarget) return;

    const incomingIds = new Set(
      Array.from(streamElement.templateContent.querySelectorAll("[data-slide-uuid]"))
        .map((el) => el.dataset.slideUuid)
    );
    const exiting = this.slideTargets.filter(
      (slide) => slide.dataset.slideUuid && !incomingIds.has(slide.dataset.slideUuid)
    );
    if (!exiting.length) return;

    exiting.forEach((slide) => {
      this.knownSlideIds.delete(slide.dataset.slideUuid);
      // Add BOTH classes synchronously, starting already "open" (1fr) --
      // this matches the slide's current on-screen block-layout height
      // exactly (see the CSS comment), so switching to the grid mechanism
      // here causes no visual jump before the collapse begins.
      slide.classList.add("airdrop-viewer__slide--animating", "is-open");
    });

    await new Promise((resolve) => requestAnimationFrame(resolve));
    exiting.forEach((slide) => slide.classList.remove("is-open"));

    await Promise.all(exiting.map((slide) => this.waitForTransitionEnd(slide)));
  }

  // Resolves on the element's first transitionend, or after
  // SLIDE_ANIMATION_MS regardless -- a transition that never fires (e.g. a
  // backgrounded tab throttling rAF/transitions) must never hang the
  // structural replace forever.
  waitForTransitionEnd(element) {
    return new Promise((resolve) => {
      const done = () => resolve();
      element.addEventListener("transitionend", done, { once: true });
      setTimeout(done, SLIDE_ANIMATION_MS + 100);
    });
  }

  // Card #2643 correction -- marks the INCOMING replacement content (still
  // just a template, not yet in the DOM) transparent, so the instant
  // defaultRender inserts it it starts invisible rather than popping in at
  // full opacity for a frame before playDeckFadeIn below can react. Same
  // two-frame trick animateSlideEntrance/animateSlidesExiting already use
  // above, one level up -- a single whole-container opacity fade instead of
  // a per-slide grid-rows animation (Jay's ask: understated, not a
  // synchronised collapse/regrow of every slide).
  prepareDeckFadeIn(streamElement) {
    if (!this.hasDeckTarget) return;
    const incomingRoot = streamElement.templateContent.querySelector(`#${this.deckTarget.id}`);
    incomingRoot?.classList.add("opacity-0");
  }

  // Flips the freshly-inserted deck container from invisible to visible over
  // a short transition. Runs AFTER defaultRender, so this.deckTarget already
  // resolves to the NEW element Turbo just swapped in -- Stimulus targets
  // query the live DOM fresh every time, never a cached reference to the
  // old, now-detached element.
  playDeckFadeIn() {
    if (!this.hasDeckTarget) return;
    const deck = this.deckTarget;
    deck.classList.add("transition-opacity", "duration-300");
    requestAnimationFrame(() => deck.classList.remove("opacity-0"));
    this.waitForTransitionEnd(deck).then(() => {
      deck.classList.remove("transition-opacity", "duration-300");
    });
  }

  // The slide pages that count: present and matching BOTH active filters
  // (card #2268's review-status filter AND card #2536's feedback-state
  // filter, composed with AND logic) — the SAME list next/previous nav, the
  // counter, and the filmstrip all walk, so a filtered-out slide can never be
  // reached any of those ways.
  //
  // CARD #3204: a client-hidden slide (`.is-hidden`, application.css) is
  // deliberately NOT excluded here any more — this method only ever runs on
  // the internal show page (the client-facing render paths never send a
  // hidden slide down at all, per Deck#client_facing_slides), where a hidden
  // slide is greyed out but must stay fully navigable/editable. Previously
  // excluded `.is-hidden` slides the exact same way a filtered-out slide is
  // (both used to mean "display: none"); now that `.is-hidden` means "greyed,
  // still in flow" instead, only matchesFilters() (review/feedback status)
  // still removes a slide from this list.
  visibleSlides() {
    if (!this.hasSlideTarget) return [];
    return this.slideTargets.filter((slide) => this.matchesFilters(slide));
  }

  // Both filter dimensions must pass (card #2536, Jay: "the filters should
  // also work together... it's just all the same filters"). Extracted so
  // visibleSlides() and applyFilters() share exactly one definition of "does
  // this slide currently match" rather than repeating the AND themselves.
  matchesFilters(slide) {
    return this.matchesReviewFilter(slide) && this.matchesFeedbackFilter(slide);
  }

  // Whether one slide wrapper matches the active review-status filter. No
  // filter (blank/"All") always matches. "not_reviewed" is the URL/Stimulus
  // spelling for DeckSlideReview's blank "" status (see DecksController's
  // REVIEW_STATUS_FILTER_VALUES comment) — translated back to "" here for the
  // comparison. A slide with no `data-review-status` at all (copy=false
  // consumers, or an id-less slide server-side) reads as "" — "Not Reviewed" —
  // the same default DeckSlideReview itself answers for an unreviewed slide.
  matchesReviewFilter(slide) {
    if (!this.reviewFilterValue) return true;
    const expected = this.reviewFilterValue === "not_reviewed" ? "" : this.reviewFilterValue;
    return (slide.dataset.reviewStatus || "") === expected;
  }

  // Whether one slide wrapper matches the active feedback-state filter (card
  // #2536) — "" (no value) always matches. "all" matches ANY feedback on the
  // slide (open or resolved); "unresolved"/"resolved" match their own
  // `data-has-open-feedback`/`data-has-resolved-feedback` attribute exactly.
  // Both attributes are kept live by onSlideFeedbackUpdated below, the same
  // way matchesReviewFilter's `data-review-status` is kept live by
  // onSlideReviewUpdated.
  matchesFeedbackFilter(slide) {
    if (!this.feedbackFilterValue) return true;
    const hasOpen = slide.dataset.hasOpenFeedback === "true";
    const hasResolved = slide.dataset.hasResolvedFeedback === "true";
    if (this.feedbackFilterValue === "all") return hasOpen || hasResolved;
    if (this.feedbackFilterValue === "unresolved") return hasOpen;
    if (this.feedbackFilterValue === "resolved") return hasResolved;
    return true;
  }

  // Live counterpart to the `data-review-status` read once at initial render
  // above (card #2268 follow-up) — decks/_slide_review_actions dispatches a
  // document-level "slide-review:updated" event (via
  // slide_review_status_controller.js) every time a status decision lands,
  // carrying the acted slide's uuid + new status. Find that slide's own
  // wrapper by `data-slide-uuid` (a matched pair with `data-review-status`,
  // both gated on `copy` in _viewer_chrome.html.erb) and refresh the cached
  // attribute the filter reads, then re-apply the filter so an already-active
  // choice (e.g. "Approved") picks up the change immediately — no reload.
  // A no-op guard skips the refresh when nothing actually changed (e.g. the
  // very first connect, where the event just echoes the server-rendered
  // value already on the wrapper).
  onSlideReviewUpdated(event) {
    if (this.index === undefined) return;
    if (!this.hasSlideTarget) return;
    const { slideUuid, status } = event.detail || {};
    if (!slideUuid) return;

    const slide = this.slideTargets.find((target) => target.dataset.slideUuid === slideUuid);
    if (!slide) return;

    const newStatus = status || "";
    if (slide.dataset.reviewStatus === newStatus) return;

    slide.dataset.reviewStatus = newStatus;
    this.refresh();
  }

  // Live counterpart to `data-has-open-feedback`/`data-has-resolved-feedback`,
  // read once at initial render (card #2536) — the same live-sync pattern as
  // onSlideReviewUpdated above, one level further. slide_feedback_status_
  // controller.js dispatches a document-level "slide-feedback:updated" event
  // every time decks/_slide_feedback_dot (re)connects — i.e. every comment
  // post AND every resolved-toggle, via deck_slide_feedbacks/_refresh.turbo_
  // stream.erb — carrying the acted slide's uuid + its two fresh booleans.
  // Find that slide's own wrapper by `data-slide-uuid` and refresh the cached
  // attributes the feedback filter reads, then re-apply filters so an
  // already-active feedback filter (e.g. "Open") picks up the change
  // immediately — no reload. Without this, the feedback filter would be a
  // real quality regression from the review-status filter it sits alongside.
  onSlideFeedbackUpdated(event) {
    if (this.index === undefined) return;
    if (!this.hasSlideTarget) return;
    const { slideUuid, hasOpenFeedback, hasResolvedFeedback } = event.detail || {};
    if (!slideUuid) return;

    const slide = this.slideTargets.find((target) => target.dataset.slideUuid === slideUuid);
    if (!slide) return;

    const newOpen = hasOpenFeedback ? "true" : "false";
    const newResolved = hasResolvedFeedback ? "true" : "false";
    if (slide.dataset.hasOpenFeedback === newOpen && slide.dataset.hasResolvedFeedback === newResolved) return;

    slide.dataset.hasOpenFeedback = newOpen;
    slide.dataset.hasResolvedFeedback = newResolved;
    this.refresh();
  }

  // Toggle each slide out of the deck flow (and therefore the filmstrip, since
  // buildFilmstrip walks visibleSlides()) when it fails EITHER active filter
  // — review-status or feedback-state (card #2536, composes two independent
  // dimensions with AND logic via matchesFilters) — via its own
  // `is-review-filtered` class (see application.css), so a filtered-out slide
  // disappears from the stacked page too, not just nav. Renamed from
  // applyReviewFilter: same class toggle, now driven by the combined check.
  // NOTE (card #3204): `is-hidden` is a DIFFERENT, unrelated class now — a
  // client-hidden slide is greyed, not removed from flow — so this method
  // never touches it; only `is-review-filtered` means "out of flow" today.
  applyFilters() {
    if (!this.hasSlideTarget) return;
    this.slideTargets.forEach((slide) => {
      slide.classList.toggle("is-review-filtered", !this.matchesFilters(slide));
    });
    if (this.hasReviewFilterButtonTarget) {
      this.reviewFilterButtonTargets.forEach((button) => {
        const active = button.dataset.reviewFilterOption === this.reviewFilterValue;
        // `border-sapphire` ONLY — no `ring-1`/`ring-sapphire` alongside it
        // (card #2309 second pass). This mirrors the initial server-rendered
        // class list in _viewer_chrome.html.erb exactly; stacking a solid
        // `border` with a non-inset `ring` on the same rounded-full edge
        // draws two concentric 1px outlines that show as a stray line
        // cutting through the pill's border. This client-side toggle is
        // WHY the bug survived an ERB-only fix during the second pass: it
        // re-ran on every Stimulus connect/refresh and re-added the ring
        // straight back over the corrected server-rendered markup.
        button.classList.toggle("border-sapphire", active);
        button.classList.toggle("border-gray-200", !active);
      });
    }
    // Feedback pills (card #2536) — same active-highlight mechanism as the
    // review-status pills above, entirely independent: both pill groups can
    // show their own active state simultaneously.
    if (this.hasFeedbackFilterButtonTarget) {
      this.feedbackFilterButtonTargets.forEach((button) => {
        const active = button.dataset.feedbackFilterOption === this.feedbackFilterValue;
        button.classList.toggle("border-sapphire", active);
        button.classList.toggle("border-gray-200", !active);
      });
    }
  }

  // Filter pill click (data-action="airdrop-report-viewer#setReviewFilter",
  // data-airdrop-report-viewer-review-filter-param="…" on each pill in
  // _viewer_chrome.html.erb). Setting the value fires reviewFilterValueChanged
  // below via Stimulus's Values API.
  setReviewFilter(event) {
    this.reviewFilterValue = event.params?.reviewFilter ?? "";
  }

  // Re-apply the filter, jump back to the first matching slide (never leave
  // the viewer parked on an index that just filtered itself away), and mirror
  // the choice into the URL so a bookmark/share/refresh starts the same way.
  // Guarded against firing before connect() has set this.index (Stimulus may
  // invoke a value's *Changed callback for its initial value ahead of
  // connect() — connect()'s own refresh() call picks up that initial value
  // regardless, so skipping here is safe, not a missed update).
  reviewFilterValueChanged() {
    if (this.index === undefined) return;
    this.index = 0;
    this.refresh();
    this.updateFilterQueryParam("review_status", this.reviewFilterValue);
  }

  // Feedback pill click (data-action="airdrop-report-viewer#setFeedbackFilter",
  // data-airdrop-report-viewer-feedback-filter-param="…" on each pill in
  // decks/_feedback_pills.html.erb). Unlike the review-status pills — which
  // have their own dedicated blank "All" pill — the three feedback pills
  // (Feedback/Open/Resolved) have no separate "clear" pill of their own, so
  // clicking the ALREADY-active pill again clears the filter back to blank.
  // This mirrors the retired feedback_filter_controller.js#select's own
  // click-to-toggle-off behaviour (judgement call, card #2536 — flag if a
  // dedicated "show every slide" pill would read better in practice).
  setFeedbackFilter(event) {
    const chosen = event.params?.feedbackFilter ?? "";
    this.feedbackFilterValue = chosen === this.feedbackFilterValue ? "" : chosen;
  }

  // Mirrors reviewFilterValueChanged. ADDITIONALLY (card #2536, Jay: "make it
  // like you've also clicked on the feedback tab for each slide... so you can
  // just scroll down") forces open every slide's Feedback panel the instant a
  // non-blank feedback filter is chosen — see openFeedbackOnMatchingSlides —
  // so scrolling through the filtered deck never needs a per-slide click.
  // Clearing the filter resets any force-opened panel back to the front view
  // (see resetForcedFeedbackPanels for why).
  feedbackFilterValueChanged() {
    if (this.index === undefined) return;
    this.index = 0;
    this.refresh();
    this.updateFilterQueryParam("feedback_status", this.feedbackFilterValue);

    if (this.feedbackFilterValue) {
      this.openFeedbackOnMatchingSlides();
    } else {
      this.resetForcedFeedbackPanels();
    }
  }

  // Card #2536: find each currently-matching slide's own deck-slide-flip
  // controller instance and force its Feedback panel open. The flip
  // controller lives on a WRAPPER inside the slide element
  // (data-controller="copy-slide deck-slide-flip", _viewer_chrome.html.erb),
  // not on the slide wrapper itself (a Stimulus Controller instance, not a
  // DOM element — so it must be looked up per slide via Stimulus's own
  // application registry, not assumed to be a target of THIS controller).
  openFeedbackOnMatchingSlides() {
    this.visibleSlides().forEach((slide) => {
      this.flipControllerFor(slide)?.showPanel("feedback");
    });
  }

  // Filter cleared (card #2536) — don't leave every slide on the page stuck
  // open on Feedback once the user is done browsing by feedback state. Resets
  // every slide (not just the currently-visible ones — the review-status
  // filter may have hidden some independently) back to the front slide view.
  // A slide the user has since flipped to Notes/Feedback manually is reset
  // too — in-memory-only panel state (deck_slide_flip_controller.js) has no
  // way to distinguish "forced open by this filter" from "the user did it
  // themself" without extra bookkeeping, so reset-to-front-on-clear is the
  // simpler default (judgement call — flag if leave-as-is reads better).
  resetForcedFeedbackPanels() {
    this.slideTargets.forEach((slide) => {
      this.flipControllerFor(slide)?.showPanel("front");
    });
  }

  // Resolve a slide wrapper's own deck-slide-flip Stimulus controller
  // instance (see openFeedbackOnMatchingSlides' comment for why this can't be
  // a plain target lookup).
  flipControllerFor(slide) {
    const wrapper = slide.querySelector('[data-controller~="deck-slide-flip"]');
    if (!wrapper) return null;
    return this.application.getControllerForElementAndIdentifier(wrapper, "deck-slide-flip");
  }

  // Reflect an active filter in the URL via history.replaceState — no
  // navigation, no new history entry, matching the existing #slide-N
  // deep-link convention (updateHash below). Shared by reviewFilterValueChanged
  // (`review_status`, card #2268) and feedbackFilterValueChanged
  // (`feedback_status`, card #2536) — generalised from the review-status-only
  // updateReviewFilterQueryParam so both filters follow one implementation.
  updateFilterQueryParam(paramName, value) {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(paramName, value);
    } else {
      url.searchParams.delete(paramName);
    }
    history.replaceState(null, "", url);
  }

  // Manual refresh button (card #2633, reworked card #2643) -- the cheap
  // fallback alongside the live broadcast
  // (AiTools::BaseService#broadcast_deck_refresh! / #broadcast_slide_refresh!):
  // a dropped ActionCable connection, or an edit made in another tab before
  // this one connected, never self-heals on its own.
  //
  // Card #2643: this used to be `Turbo.visit(window.location.href, { action:
  // "replace" })` -- a same-URL Drive visit, which application.html.erb's
  // `turbo_refreshes_with method: :morph` turns into a MORPH refresh rather
  // than a hard reload (see the old comment preserved on
  // persist_tabs_controller.js's own turbo:morph listener for how THAT was
  // diagnosed). Idiomorph has no stable keys to diff the deck viewer's many
  // absolutely-positioned canvas elements/images against their pre-morph
  // versions, so in practice a large fraction of the page got touched at
  // once -- Jay saw that as a full-page flash. This replaces it with a
  // narrowly-scoped fetch of DecksController#refresh_slides (a plain Turbo
  // Stream response, no Drive navigation involved at all) applied via the
  // documented Turbo.renderStreamMessage API -- the response replaces ONLY
  // "deck_#{id}_slides" (decks/_slide_list.html.erb's own wrapper), the same
  // target broadcast_deck_refresh! itself replaces, so nothing else on the
  // page (toolbar, tabs, filmstrip panel) ever re-renders. Confirms this
  // controller's own turbo:morph listener above is now unreachable FROM THIS
  // BUTTON -- see that listener's comment for whether anything else on this
  // page can still trigger a morph.
  //
  // Named reloadPage, NOT refresh -- this controller already has a `refresh()`
  // method (re-scan the slide pages / re-render chrome, used throughout: nav,
  // filters, live-update handlers). A same-named second method here would
  // silently OVERWRITE that one (last class member wins, no error, confirmed
  // the hard way -- every internal `this.refresh()` call started firing a
  // full-page Turbo.visit instead of its own re-scan, breaking the review/
  // feedback filters' URL sync and active-pill highlight).
  //
  // Card #2643 CORRECTION: the div-scoping above was right -- Jay confirmed
  // live that only "deck_#{id}_slides" re-renders now, no more full-page
  // morph -- but resetting knownSlideIds to empty here (so every slide
  // replayed animateSlideEntrance's grow-in, "prove everything refreshed")
  // was the actual cause of the flash Jay still saw: several slides all
  // collapsing to 0fr and regrowing to 1fr in the same frame blanks the
  // whole slide-list area for a beat. That reset is gone. Jay's confirmed
  // direction instead: a spinner INSIDE the slide-list area while this fetch
  // is in flight (showRefreshOverlay/hideRefreshOverlay, the sticky
  // `refreshOverlay` target in decks/_viewer_chrome.html.erb), then a clean,
  // understated fade-in of the WHOLE container once the fresh content lands
  // (prepareDeckFadeIn/playDeckFadeIn via handleBeforeStreamRender's
  // isManualReload branch) -- never a per-slide collapse/regrow on this
  // path. animateSlideEntrance goes back to being reserved for genuinely NEW
  // slides on the automatic structural broadcast, exactly as card #2633
  // first built it, completely unaffected by any of this.
  //
  // Card #2643 THIRD ROUND (three refinements, watching it live again):
  //   1. Minimum display duration -- the fetch is often so fast (~50ms
  //      locally) that the spinner barely registers. waitOutMinimumDisplay
  //      enforces a MIN_REFRESH_DISPLAY_MS floor -- total visible loading
  //      time is max(actualFetchDuration, floor), never a fixed extra delay
  //      on top of a slow fetch.
  //   2. (Padding bump lives in decks/_viewer_chrome.html.erb's own overlay
  //      pill markup, not here.)
  //   3. Hide the OLD content the INSTANT the button is clicked
  //      (hideDeckForReload), not just once the fresh content is ready --
  //      the whole slide area now reads as "actively loading" (blank + a
  //      visible pill) for the FULL loading window, not "static content
  //      with a floating pill doing nothing visible on top of it". Restored
  //      immediately on any failure (revealDeckAfterReloadFailure) so a
  //      broken fetch never leaves the deck permanently blank.
  async reloadPage(event) {
    const url = event.params?.refreshUrl;
    if (!url) return;

    const icon = event.currentTarget.querySelector("svg");
    icon?.classList.add("animate-spin");
    this.showRefreshOverlay();
    this.hideDeckForReload();

    // Read synchronously by handleBeforeStreamRender the instant the stream
    // THIS fetch triggers lands -- see that method's own comment for why
    // resetting it back to false in `finally` below can never race the read.
    this.pendingManualReload = true;
    const startedAt = performance.now();

    try {
      const response = await fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" } });
      if (response.ok) {
        const html = await response.text();
        // Wait out whatever's left of the minimum floor BEFORE applying the
        // stream -- the old content (already hidden by hideDeckForReload
        // above) and the overlay pill both stay exactly as they are for
        // this entire wait, so the floor covers the WHOLE loading window,
        // not just the fetch itself.
        await this.waitOutMinimumDisplay(startedAt);
        Turbo.renderStreamMessage(html);
      } else {
        // A non-OK response never reaches handleBeforeStreamRender (no
        // stream to render), so the overlay would otherwise hang forever --
        // and the hidden deck would otherwise stay blank forever too.
        this.hideRefreshOverlay();
        this.revealDeckAfterReloadFailure();
      }
    } catch (error) {
      // Network failure -- same reasoning as the non-OK branch above.
      this.hideRefreshOverlay();
      this.revealDeckAfterReloadFailure();
    } finally {
      icon?.classList.remove("animate-spin");
      this.pendingManualReload = false;
    }
  }

  // Card #2643 third round -- resolves once at least MIN_REFRESH_DISPLAY_MS
  // has elapsed since `startedAt` (a performance.now() timestamp taken the
  // instant the fetch fired). A floor, not a fixed delay: if the fetch
  // already took that long or longer, resolves immediately with no extra
  // wait tacked on top. performance.now() is the idiomatic choice for
  // measuring an elapsed duration in browser JS (monotonic, sub-millisecond,
  // immune to wall-clock adjustments) -- Date.now() would work fine too over
  // a window this short, but this is what performance.now() is for.
  waitOutMinimumDisplay(startedAt) {
    const remaining = MIN_REFRESH_DISPLAY_MS - (performance.now() - startedAt);
    if (remaining <= 0) return Promise.resolve();
    return new Promise((resolve) => setTimeout(resolve, remaining));
  }

  // Card #2643 third round -- hides the CURRENT (pre-refresh) deck content
  // the instant reloadPage fires, synchronously, before the fetch even
  // starts. Deliberately no transition class here (contrast with
  // playDeckFadeIn's fade-IN, which DOES want one) -- Jay's ask was for the
  // slide area to go blank "the instant" the button is clicked, an abrupt
  // cut, not a fade. `this.deckTarget` resolves fresh via Stimulus's target
  // lookup, so calling this BEFORE the stream lands means it grabs the OLD
  // element that's still on screen, not whatever eventually replaces it.
  hideDeckForReload() {
    if (!this.hasDeckTarget) return;
    this.deckTarget.classList.add("opacity-0");
  }

  // Error path (non-OK response or a network failure) -- the turbo_stream
  // replace never happened, so `this.deckTarget` still resolves to the SAME
  // element hideDeckForReload hid above. Strips the class back off so the
  // existing (unchanged, still correct) content reappears immediately,
  // rather than leaving the deck permanently blank after a failed refresh.
  revealDeckAfterReloadFailure() {
    if (!this.hasDeckTarget) return;
    this.deckTarget.classList.remove("opacity-0");
  }

  // Card #2643 correction -- shows/hides the sticky spinner pill floating
  // over the slide-list area (decks/_viewer_chrome.html.erb's
  // `refreshOverlay` target, a SIBLING of the deck target living outside the
  // replaced element -- it must survive every turbo_stream.replace
  // untouched, not be destroyed and recreated hidden along with the slides
  // each time). Only the `hidden` class is ever toggled; `flex` stays
  // permanently in the class list -- this app's Tailwind build emits
  // `.hidden{display:none}` AFTER `.flex{display:flex}` (confirmed against
  // app/assets/builds/tailwind.css, not assumed), so `hidden` always wins the
  // cascade when both are present.
  showRefreshOverlay() {
    if (this.hasRefreshOverlayTarget) this.refreshOverlayTarget.classList.remove("hidden");
  }

  hideRefreshOverlay() {
    if (this.hasRefreshOverlayTarget) this.refreshOverlayTarget.classList.add("hidden");
  }

  // ---- Navigation --------------------------------------------------------

  next() { this.goTo(this.index + 1); }
  prev() { this.goTo(this.index - 1); }
  first() { this.goTo(0); }
  last() { this.goTo(this.visibleSlides().length - 1); }

  goTo(target) {
    const slides = this.visibleSlides();
    if (!slides.length) return;
    this.index = Math.max(0, Math.min(target, slides.length - 1));
    this.render();
    this.scrollToActive();
    this.updateHash();
  }

  // Jump from a filmstrip thumbnail or a click in the deck.
  jumpToSlide(event) {
    const wrapper = event.currentTarget.closest("[data-airdrop-report-viewer-target='thumb']");
    const index = wrapper ? parseInt(wrapper.dataset.index, 10) : NaN;
    if (Number.isFinite(index)) this.goTo(index);
  }

  // ---- Render (counter, progress, active highlight) ----------------------

  render() {
    const slides = this.visibleSlides();
    const count = slides.length;
    const human = count ? this.index + 1 : 0;

    // There may be two counters (toolbar + slideshow nav); keep both in sync.
    if (this.hasCounterTarget) {
      this.counterTargets.forEach((node) => { node.textContent = `${human} / ${count}`; });
    }
    if (this.hasProgressBarTarget) {
      const pct = count ? (human / count) * 100 : 0;
      this.progressBarTarget.style.width = `${pct}%`;
    }
    if (this.hasPrevButtonTarget) this.prevButtonTarget.disabled = this.index <= 0;
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = this.index >= count - 1;

    // Active highlight in the filmstrip.
    if (this.hasThumbTarget) {
      this.thumbTargets.forEach((thumb) => {
        const active = parseInt(thumb.dataset.index, 10) === this.index;
        thumb.classList.toggle("ring-2", active);
        thumb.classList.toggle("ring-sapphire", active);
      });
    }

    // In slideshow only one slide is shown; mark the active one.
    if (this.presenting) {
      slides.forEach((slide, i) => slide.classList.toggle("is-pres-active", i === this.index));
    }
  }

  // CARD #2903 BUG 1 — a right-click (or any click) landing in the top ~130px
  // of whichever slide just scrolled into view almost never hit its target,
  // on the FIRST attempt after a fresh page load above all. Root cause (FIDO,
  // reproduced with real Playwright clicks against production deck #82): this
  // page's sticky header chrome — decks/show.html.erb's `#deck-tabs`,
  // decks/_review_status_filter.html.erb's filter row, and this chrome's own
  // toolbar below (_viewer_chrome.html.erb, all three `position: sticky`,
  // `z-20`, stacked one under the next — see their own header comments,
  // card #2309) — stays PINNED at the top of the SAME scrolling container
  // that `scrollIntoView({ block: "start" })` below aligns a slide flush
  // against. Every call that lands here — next/prev, a filmstrip click, AND
  // `jumpToHash()` on a fresh page load with a `#slide-N` deep link already
  // in the URL — leaves the top of that slide sitting directly UNDER up to
  // ~130px of chrome that is still very much interactive (z-20, on top),
  // silently eating any click on whatever the slide had there. `scroll-
  // margin-top` is `scrollIntoView`'s own native offset for exactly this
  // "sticky header covers the thing I just scrolled to" pattern — set fresh
  // on every call (not cached) since the header stack's rendered height can
  // itself change between calls (e.g. the review-status filter's pill row
  // wrapping onto a second line at a narrower viewport) — see
  // stickyHeaderHeight's own comment.
  scrollToActive() {
    if (this.presenting) return; // presentation pins the active slide.
    const slides = this.visibleSlides();
    const active = slides[this.index];
    if (!active) return;

    active.style.scrollMarginTop = `${this.stickyHeaderHeight()}px`;
    active.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Total rendered height of the sticky header chrome that can sit ABOVE the
  // deck within the SAME scrolling container (card #2903 Bug 1). Only three
  // elements in the whole app are ever sticky in this stack (confirmed via a
  // codebase-wide grep — see this card's own build notes): `#deck-tabs`
  // (decks/show.html.erb, page-level chrome OUTSIDE this controller's own
  // root, hence the global lookup rather than an in-scope query),
  // `.airdrop-review-filter` (decks/_review_status_filter.html.erb, gated on
  // `copy`), and this chrome's own toolbar (the `toolbarSticky` target below,
  // only rendered when `toolbar_sticky_top` is set). Summing whichever are
  // actually present on THIS page covers every caller without branching:
  // the deck show page renders all three, present.html.erb renders the
  // toolbar alone (no tabs/filter above it there), and every other consumer
  // (the deck-editor Preview tab, shareables, the TV kiosk) renders none, so
  // this stays 0 and scrollToActive's offset is a no-op exactly as before
  // this fix. Measured fresh on every call rather than cached once — the
  // whole point is to track the chrome's ACTUAL rendered height instead of
  // yet another hardcoded pixel guess (three of those already drifted out of
  // sync across card #2309's first, second, and third passes).
  stickyHeaderHeight() {
    let height = 0;

    const tabs = document.getElementById("deck-tabs");
    if (tabs) height += tabs.getBoundingClientRect().height;

    const filter = document.querySelector(".airdrop-review-filter");
    if (filter) height += filter.getBoundingClientRect().height;

    if (this.hasToolbarStickyTarget) height += this.toolbarStickyTarget.getBoundingClientRect().height;

    return height;
  }

  // ---- Fullscreen --------------------------------------------------------

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (this.element.requestFullscreen) {
      this.element.requestFullscreen().catch(() => {});
    }
  }

  // Keep the on-screen state honest when the browser exits fullscreen for us
  // (Esc, F11). If we were presenting and fullscreen ended, drop slideshow too.
  syncFullscreenState() {
    const isFull = !!document.fullscreenElement;
    if (this.hasFullscreenButtonTarget) {
      this.fullscreenButtonTarget.setAttribute("aria-pressed", isFull ? "true" : "false");
    }
    if (!isFull && this.presenting) this.exitSlideshow();
  }

  // ---- Slideshow / presentation -----------------------------------------

  toggleSlideshow() {
    if (this.presenting) this.exitSlideshow();
    else this.enterSlideshow();
  }

  enterSlideshow() {
    if (!this.visibleSlides().length) return;
    this.presenting = true;
    this.element.classList.add("is-presenting");
    this.applyPresScale();
    this.render();
    this.resetIdleTimer();

    // Entering presentation auto-requests fullscreen (matches the original).
    if (this.fullscreenValue && !document.fullscreenElement && this.element.requestFullscreen) {
      this.element.requestFullscreen().catch(() => {});
    }
  }

  exitSlideshow() {
    this.presenting = false;
    this.element.classList.remove("is-presenting");
    this.element.classList.remove("is-idle");
    clearTimeout(this.idleTimer);
    this.visibleSlides().forEach((slide) => slide.classList.remove("is-pres-active"));
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    this.render();
  }

  // The top toolbar is hidden entirely while presenting (card #2171), so the
  // full viewport height is available to the slide — no chrome reserve needed.
  applyPresScale() {
    const scale = Math.min(
      window.innerWidth / this.canvasW,
      window.innerHeight / this.canvasH
    );
    this.element.style.setProperty("--airdrop-pres-scale", String(scale));
  }

  // ---- Idle-fade (card #2171) --------------------------------------------
  // PowerPoint-style: the bottom pres-nav cluster fades after a few seconds of
  // no pointer movement while presenting, and returns to full opacity the
  // moment the pointer moves again. Same cluster, same styling — this only
  // toggles a class the CSS uses for the opacity transition.

  resetIdleTimer() {
    if (!this.presenting) return;
    this.element.classList.remove("is-idle");
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.element.classList.add("is-idle");
    }, PRES_IDLE_MS);
  }

  // Click anywhere on the presentation stage (the deck) advances. The on-screen
  // prev/next/exit buttons live in the pres-nav cluster, a sibling of the deck —
  // not inside it — so their clicks never reach this handler.
  //
  // But a slide may carry its own interactive content (a link or button — show
  // and shareable decks can embed them). A click on that content must do its own
  // thing, not be swallowed by the advance, so bail before advancing when the
  // click landed on (or inside) an interactive element.
  advanceOnClick(event) {
    if (!this.presenting) return;
    if (event.target.closest("a, button, input, textarea, select")) return;
    this.next();
  }

  // ---- Print -------------------------------------------------------------

  // In-place print button. Awaits the same readiness check as auto-print
  // (card #2167) for consistency, even though on this path the page is already
  // fully loaded and visible to the user before they can click it — the wait
  // resolves near-instantly here, it's cheap insurance rather than a real fix.
  async print() {
    await this.waitUntilPrintReady();
    window.print();
  }

  // Honour a ?print=1 request by triggering the print dialog once the page has
  // fully loaded (fonts + slide background images painted). Guarded so a single
  // viewer never prints twice, and suppressed in kiosk mode.
  async maybeAutoPrint() {
    if (this.kioskValue || this.autoPrinted) return;
    const wants = new URLSearchParams(window.location.search).get("print");
    if (!wants) return;

    this.autoPrinted = true;
    const fire = async () => {
      await this.waitUntilPrintReady();
      requestAnimationFrame(() => window.print());
    };
    if (document.readyState === "complete") fire();
    else window.addEventListener("load", fire, { once: true });
  }

  // Best-effort wait for webfonts + embedded analytics iframes before printing
  // (card #2167). window.load fires as soon as the document + its own images are
  // painted — it does NOT wait for @font-face swaps (Raleway/Calibri, loaded in
  // the layout) or for a live analytics iframe's OWN async Chart.js render, so
  // printing straight off `load` was racing both and silently falling back to
  // system fonts / blank chart areas depending on cache and network speed.
  //
  // - document.fonts.ready resolves once every requested font has finished
  //   loading and the browser has done a layout pass with it.
  // - Each iframe (the analytics embed — see decks/slides/_canvas.html.erb) gets
  //   its own `load` listener, capped by IFRAME_TIMEOUT_MS so one slow or broken
  //   embed can never hang the print dialog forever.
  // - A short grace delay AFTER the iframe loads resolve, only when iframes are
  //   present: an iframe's `load` event covers its document load, not its own
  //   post-load AJAX chart paint, so this narrows (does not fully eliminate)
  //   the analytics race.
  async waitUntilPrintReady() {
    const waits = [];
    if (document.fonts?.ready) waits.push(document.fonts.ready);

    const iframes = Array.from(this.element.querySelectorAll("iframe"));
    const IFRAME_TIMEOUT_MS = 4000;
    iframes.forEach((iframe) => {
      waits.push(new Promise((resolve) => {
        // contentDocument is same-origin here (the analytics embed is a WIN
        // dashboard URL — see _canvas.html.erb), so this is safe to read
        // directly, but guard defensively in case that ever changes.
        if (iframe.contentDocument?.readyState === "complete") return resolve();
        const done = () => resolve();
        iframe.addEventListener("load", done, { once: true });
        setTimeout(done, IFRAME_TIMEOUT_MS);
      }));
    });

    await Promise.all(waits);
    if (iframes.length) await new Promise((resolve) => setTimeout(resolve, 800));
  }

  // ---- Filmstrip ---------------------------------------------------------

  toggleFilmstrip() {
    if (!this.hasFilmstripPanelTarget) return;
    this.filmstripPanelTarget.classList.toggle("hidden");
  }

  // Build scaled thumbnail clones of each visible slide. Clones are inert
  // (pointer-events disabled on the slide itself; the wrapping button handles
  // the click) so a thumbnail is a faithful, non-interactive miniature.
  buildFilmstrip() {
    if (!this.hasFilmstripTarget) return;
    this.filmstripTarget.replaceChildren();

    // Tear down any observers from the previous build so they don't leak or
    // fire against detached tiles. We re-observe each fresh tile below.
    this.filmstripObserver?.disconnect();
    this.filmstripObserver = this.buildFilmstripObserver();

    this.visibleSlides().forEach((slide, index) => {
      const source = slide.querySelector(".airdrop-slide");
      if (!source) return;

      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "airdrop-viewer__thumb relative block w-full overflow-hidden rounded border border-gray-300 bg-white";
      button.style.aspectRatio = `${this.canvasW} / ${this.canvasH}`;
      button.dataset.index = String(index);
      button.dataset.action = "click->airdrop-report-viewer#jumpToSlide";
      button.setAttribute("data-airdrop-report-viewer-target", "thumb");
      button.setAttribute("aria-label", `Go to slide ${index + 1}`);

      // A scaled, inert clone of the slide canvas. The full-canvas clone is
      // sized to the deck's own canvas (ADR-0077, default 1280x720) and scaled
      // down to fill the tile. The scale factor is the tile's rendered width
      // over the canvas width — a unitless <number> applied by the
      // ResizeObserver below (CSS scale() rejects lengths, so it cannot be
      // computed declaratively from a container-query unit). Because the button
      // carries aspect-ratio = canvasW/canvasH, scaling by width/canvasW makes
      // the clone exactly fill the tile. The observer recomputes on reveal
      // (panel goes from display:none → real width) and on any grid/window
      // resize.
      const scale = document.createElement("div");
      scale.style.cssText =
        `position:absolute;top:0;left:0;width:${this.canvasW}px;height:${this.canvasH}px;` +
        `transform-origin:top left;pointer-events:none;`;

      const clone = source.cloneNode(true);
      clone.style.transform = "none";
      // Strip every id from the clone (deck #28 incident follow-up, third
      // round): cloneNode(true) duplicates id attributes verbatim, so any
      // element inside .airdrop-slide carrying one (e.g.
      // decks/_slide_feedback_pins.html.erb's turbo_stream target) now exists
      // TWICE on the page. getElementById -- which is exactly what a
      // turbo_stream.replace target resolves through -- is free to return
      // either match, and in practice returned THIS inert filmstrip
      // thumbnail (it sits earlier in document order than the real slide),
      // so the live slide silently never updated while the invisible clone
      // did. This thumbnail is a purely visual, pointer-events:none preview
      // -- nothing ever needs to address it by id -- so stripping is a pure
      // subtraction with no behaviour change for the filmstrip itself.
      if (clone.id) clone.removeAttribute("id");
      clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
      scale.appendChild(clone);
      button.appendChild(scale);

      // Stash the scale element on the button so the observer callback can find
      // it from the observed entry without a DOM query.
      button._scaleEl = scale;

      const badge = document.createElement("span");
      badge.className =
        "absolute left-1 top-1 rounded bg-black/50 px-1 text-2xs font-medium text-white";
      badge.textContent = String(index + 1);
      button.appendChild(badge);

      // Review-status badge (card #2079 Phase 4, widened from a boolean
      // rejected-only badge to the full status vocabulary in the second
      // redesign round). `data-review-status` is threaded onto the slide
      // WRAPPER server-side (report.slide_review_status_for) and read fresh
      // here every time buildFilmstrip runs (every refresh() call) — a slide
      // reviewed mid-session now shows its updated filmstrip dot immediately,
      // since onSlideReviewUpdated (card #2268 follow-up) writes the new
      // status onto the wrapper and calls refresh() before this ever runs
      // again. Colours mirror the Response review card dot exactly
      // (review_status_card_dot_classes): approved=green, deciding=amber,
      // rejected=red. Blank ("Not Reviewed") shows no dot, same as before.
      const statusDotStyle = {
        approved: { color: "bg-green-500", label: "Approved" },
        deciding: { color: "bg-amber-500", label: "Under Review" },
        rejected: { color: "bg-red-500", label: "Rejected" }
      }[slide.dataset.reviewStatus];
      if (statusDotStyle) {
        const statusDot = document.createElement("span");
        statusDot.className =
          `absolute right-2 top-2 size-2.5 rounded-full ${statusDotStyle.color} ring-2 ring-white`;
        statusDot.title = statusDotStyle.label;
        button.appendChild(statusDot);
      }

      this.filmstripTarget.appendChild(button);
      this.filmstripObserver.observe(button);
    });
  }

  // A ResizeObserver that keeps each thumbnail clone scaled to fit its tile.
  // The tile starts at width 0 when the filmstrip panel is hidden; the observer
  // fires when it reveals (gains real width) and on every subsequent resize.
  buildFilmstripObserver() {
    return new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const scaleEl = entry.target._scaleEl;
        if (!scaleEl) return;
        const width = entry.contentRect.width;
        const factor = width > 0 ? width / this.canvasW : 0;
        scaleEl.style.transform = `scale(${factor})`;
      });
    });
  }

  // ---- Deep-linking ------------------------------------------------------

  // On load (and on hashchange), honour #slide-N by jumping to that slide.
  jumpToHash() {
    if (!this.deepLinkValue) return;
    const match = (window.location.hash || "").match(/^#slide-(\d+)$/);
    if (!match) return;
    const target = parseInt(match[1], 10) - 1; // 1-based in the URL.
    if (Number.isFinite(target)) this.goTo(target);
  }

  // Reflect the active slide in the URL hash without adding history entries.
  updateHash() {
    if (!this.deepLinkValue) return;
    const hash = `#slide-${this.index + 1}`;
    if (window.location.hash !== hash) {
      history.replaceState(null, "", hash);
    }
  }

  // ---- Keyboard ----------------------------------------------------------

  // One consolidated keyboard handler. Guards against firing while the user is
  // typing in a field / contentEditable (so the deck editor's text inputs and
  // JSON textarea are never hijacked) and only acts when this viewer owns the
  // interaction: always while presenting; otherwise only nav keys, and only when
  // the viewer is the sole one on the page.
  handleKeydown(event) {
    if (this.isEditingContext(event.target)) return;

    // Escape exits a mode (slideshow first; fullscreen the browser handles).
    // In kiosk mode, ESC is locked — no one should be able to drop out of
    // presentation on an unattended TV screen.
    if (event.key === "Escape") {
      if (this.presenting && !this.kioskValue) {
        event.preventDefault();
        this.exitSlideshow();
      }
      return;
    }

    // Navigation keys only act while presenting, or when nav chrome is enabled
    // and this is the only viewer surface in play. This keeps page scroll
    // working normally when a viewer is just embedded in a scroll page.
    const navActive = this.presenting || (this.navValue && this.isPrimaryViewer());
    if (!navActive) return;

    switch (event.key) {
      case "ArrowRight":
      case " ":
      case "PageDown":
        event.preventDefault();
        this.next();
        break;
      case "ArrowLeft":
      case "PageUp":
        event.preventDefault();
        this.prev();
        break;
      case "Home":
        event.preventDefault();
        this.first();
        break;
      case "End":
        event.preventDefault();
        this.last();
        break;
    }
  }

  // True when focus is in a text input, textarea, select, or contentEditable —
  // never steal keystrokes from the deck editor or any form.
  isEditingContext(node) {
    if (!node) return false;
    if (node.isContentEditable) return true;
    const tag = node.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
  }

  // Only treat nav keys as ours when this viewer is the sole one on the page AND
  // it is actually visible. On the show / shareable pages this is always true. On
  // the build page the viewer lives in the hidden Preview panel, so nav keys stay
  // inert (unless presenting) until the Preview tab is shown — editor keystrokes
  // on the Designer tab are never hijacked.
  isPrimaryViewer() {
    if (document.querySelectorAll("[data-controller~='airdrop-report-viewer']").length !== 1) {
      return false;
    }
    // offsetParent is null when the element (or an ancestor) is display:none.
    return this.element.offsetParent !== null;
  }
}
